import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api, { normalize } from '../services/api';
import CadastroDataTable from '../components/CadastroDataTable/CadastroDataTable';
import { expandModeloTexto } from '../utils/expandModeloTexto';
import './EmpresaPage.css';
import './AgentesPage.css';

const TIPOS_VAR = [
  { label: 'Texto', value: 'Texto' },
  { label: 'Data', value: 'Data' },
  { label: 'Valor', value: 'Valor' },
  { label: 'Formula', value: 'Formula' },
];

const REGRAS_DATA = [
  { label: 'Hoje', value: 'hoje' },
  { label: 'Mesmo dia +1 mês', value: 'mesmoDiaProximoMes' },
];

const EMPTY_VAR = { nome: '', tipo: 'Texto', valorPadrao: '', configJson: '', obrigatoria: false };
const EMPTY = { id: null, nome: '', conteudo: '', ativo: true, variaveis: [] };

export default function ModelosTextoPage({ embedded = false }) {
  const toast = useRef(null);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/modelos-texto');
      setItens(normalize(data));
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar modelos.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const setCampo = (c, v) => setForm((p) => ({ ...p, [c]: v }));

  const addVar = () => setForm((p) => ({ ...p, variaveis: [...(p.variaveis || []), { ...EMPTY_VAR }] }));

  const setVar = (idx, campo, valor) => {
    setForm((p) => {
      const vars = [...(p.variaveis || [])];
      vars[idx] = { ...vars[idx], [campo]: valor };
      return { ...p, variaveis: vars };
    });
  };

  const removeVar = (idx) => {
    setForm((p) => ({ ...p, variaveis: (p.variaveis || []).filter((_, i) => i !== idx) }));
  };

  const inserirPlaceholder = (nome) => {
    const token = `{{${(nome || 'VAR').toUpperCase()}}}`;
    setForm((p) => ({ ...p, conteudo: `${p.conteudo || ''}${p.conteudo ? ' ' : ''}${token}` }));
  };

  const previewExpandido = useMemo(() => {
    const vals = {};
    (form.variaveis || []).forEach((v) => {
      if (v.nome) vals[v.nome] = v.valorPadrao || `[${v.nome}]`;
    });
    return expandModeloTexto(form.conteudo, form.variaveis, vals, {
      valorServico: 1000,
      aliquotaIss: 5,
      descontoIncondicionado: 0,
      tomadorNome: 'Cliente Exemplo',
      tomadorDoc: '00.000.000/0001-00',
    });
  }, [form.conteudo, form.variaveis]);

  const salvar = async () => {
    if (!form.nome?.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe o nome do modelo.' });
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        nome: form.nome,
        conteudo: form.conteudo,
        ativo: form.ativo,
        variaveis: (form.variaveis || [])
          .filter((v) => v.nome?.trim())
          .map((v) => {
            let configJson = v.configJson;
            if (v.tipo === 'Data' && !configJson) {
              configJson = JSON.stringify({ regra: v.valorPadrao === '+1mes' ? 'mesmoDiaProximoMes' : 'hoje' });
            }
            return { ...v, configJson };
          }),
      };
      if (form.id) await api.put(`/modelos-texto/${form.id}`, payload);
      else await api.post('/modelos-texto', payload);
      toast.current?.show({ severity: 'success', summary: 'Salvo' });
      setVisible(false);
      carregar();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: e.response?.data?.message || 'Falha ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = (row) => {
    confirmDialog({
      message: `Remover modelo "${row.nome}"?`,
      header: 'Confirmação',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: async () => {
        await api.delete(`/modelos-texto/${row.id}`);
        carregar();
      },
    });
  };

  const columns = useMemo(
    () => [
      { field: 'nome', header: 'Nome' },
      { field: 'ativo', header: 'Ativo', body: (r) => (r.ativo ? 'Sim' : 'Não'), exportValue: (r) => (r.ativo ? 'Sim' : 'Não') },
      { field: '_vars', header: 'Variáveis', body: (r) => (r.variaveis || []).length, exportValue: (r) => (r.variaveis || []).length },
      {
        field: 'conteudo',
        header: 'Conteúdo',
        defaultVisible: false,
        body: (r) => (r.conteudo || '').slice(0, 80),
        exportValue: (r) => r.conteudo || '',
      },
      {
        field: '_acoes',
        header: 'Ações',
        sortable: false,
        alwaysVisible: true,
        body: (row) => (
          <div className="agentes-acoes">
            <Button
              icon="pi pi-pencil"
              rounded
              text
              onClick={() => {
                setForm({
                  ...EMPTY,
                  ...row,
                  variaveis: (row.variaveis || []).map((v) => ({ ...EMPTY_VAR, ...v })),
                });
                setVisible(true);
              }}
            />
            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => excluir(row)} />
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="agentes-page">
      <Toast ref={toast} />
      <ConfirmDialog acceptLabel="Sim" rejectLabel="Não" />
      <div className="agentes-header">
        {!embedded && <h1>Modelos de texto (discriminação)</h1>}
        {embedded && <h2 style={{ margin: 0, color: '#002478', fontSize: '1.15rem' }}>Modelos de texto</h2>}
        <Button label="Novo modelo" icon="pi pi-plus" onClick={() => { setForm(EMPTY); setVisible(true); }} />
      </div>
      <p style={{ color: '#64748b', marginTop: -8, marginBottom: '1rem' }}>
        Use placeholders no formato <code>{'{{VENCIMENTO}}'}</code>. Na emissão, o modelo expande variáveis tipadas (Texto, Data +1 mês, Valor, Fórmula).
        Contexto da NF: <code>{'{{VALORSERVICO}}'}</code>, <code>{'{{TOMADOR}}'}</code>, <code>{'{{CNPJTOMADOR}}'}</code>, <code>{'{{ALIQUOTAISS}}'}</code>.
      </p>

      <CadastroDataTable
        value={itens}
        columns={columns}
        storageKey="modelos-texto"
        loading={loading}
        title="Modelos de texto"
        emptyMessage="Nenhum modelo."
        globalFilterFields={['nome']}
      />

      <Dialog header={form.id ? 'Editar modelo' : 'Novo modelo'} visible={visible} style={{ width: 820 }} breakpoints={{ '960px': '95vw' }} onHide={() => setVisible(false)}>
        <div className="agentes-form">
          <div className="nfse-form-row">
            <div className="agentes-field agentes-field-full">
              <label>Nome</label>
              <InputText value={form.nome} onChange={(e) => setCampo('nome', e.target.value)} />
            </div>
          </div>
          <div className="nfse-form-row">
            <div className="agentes-field agentes-field-full">
              <label>Conteúdo (com {'{{VAR}}'})</label>
              <InputTextarea rows={5} value={form.conteudo} onChange={(e) => setCampo('conteudo', e.target.value)} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {(form.variaveis || []).filter((v) => v.nome).map((v) => (
                  <Button
                    key={v.nome}
                    type="button"
                    label={`Inserir {{${v.nome}}}`}
                    className="p-button-sm p-button-outlined"
                    onClick={() => inserirPlaceholder(v.nome)}
                  />
                ))}
                <Button type="button" label="Inserir {{VALORSERVICO}}" className="p-button-sm p-button-text" onClick={() => inserirPlaceholder('VALORSERVICO')} />
                <Button type="button" label="Inserir {{TOMADOR}}" className="p-button-sm p-button-text" onClick={() => inserirPlaceholder('TOMADOR')} />
              </div>
            </div>
          </div>
          <div className="nfse-form-row">
            <div className="agentes-field agentes-field-checkbox">
              <Checkbox checked={form.ativo} onChange={(e) => setCampo('ativo', e.checked)} inputId="ativo" />
              <label htmlFor="ativo">Ativo</label>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Variáveis</strong>
            <Button label="Adicionar" icon="pi pi-plus" className="p-button-sm p-button-outlined" onClick={addVar} />
          </div>
          {(form.variaveis || []).map((v, idx) => (
            <div key={idx} className="nfse-form-row" style={{ marginTop: 8, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
              <div className="agentes-field">
                <label>Nome (sem chaves)</label>
                <InputText value={v.nome} placeholder="VENCIMENTO" onChange={(e) => setVar(idx, 'nome', e.target.value)} />
              </div>
              <div className="agentes-field">
                <label>Tipo</label>
                <Dropdown value={v.tipo} options={TIPOS_VAR} onChange={(e) => setVar(idx, 'tipo', e.value)} />
              </div>
              {v.tipo === 'Data' ? (
                <div className="agentes-field">
                  <label>Regra de data</label>
                  <Dropdown
                    value={(() => {
                      try {
                        const c = v.configJson ? JSON.parse(v.configJson) : {};
                        return c.regra || 'mesmoDiaProximoMes';
                      } catch {
                        return 'mesmoDiaProximoMes';
                      }
                    })()}
                    options={REGRAS_DATA}
                    onChange={(e) => setVar(idx, 'configJson', JSON.stringify({ regra: e.value }))}
                  />
                </div>
              ) : (
                <div className="agentes-field">
                  <label>{v.tipo === 'Formula' ? 'Expressão / padrão' : 'Valor padrão'}</label>
                  <InputText
                    value={v.valorPadrao}
                    placeholder={v.tipo === 'Formula' ? '<VALORSERVICO>*0.06' : ''}
                    onChange={(e) => setVar(idx, 'valorPadrao', e.target.value)}
                  />
                </div>
              )}
              <div className="agentes-field agentes-field-checkbox">
                <Checkbox checked={v.obrigatoria} onChange={(e) => setVar(idx, 'obrigatoria', e.checked)} inputId={`ob-${idx}`} />
                <label htmlFor={`ob-${idx}`}>Obrigatória</label>
              </div>
              <div className="agentes-field" style={{ flex: '0 0 auto', minWidth: 'auto', justifyContent: 'flex-end' }}>
                <label>&nbsp;</label>
                <Button icon="pi pi-trash" className="p-button-danger p-button-text" onClick={() => removeVar(idx)} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <strong>Preview expandido</strong>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f1f5f9', padding: 12, borderRadius: 8, marginTop: 8 }}>
            {previewExpandido || '—'}
          </pre>
        </div>

        <div className="agentes-dialog-footer">
          <Button label="Cancelar" className="p-button-text" onClick={() => setVisible(false)} />
          <Button label="Salvar" icon="pi pi-save" loading={salvando} onClick={salvar} />
        </div>
      </Dialog>
    </div>
  );
}
